import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, ArrowLeft, Plus, X, Upload, Eye, Bold, Italic, List, ListOrdered, Heading1, Heading2, Link as LinkIcon, Image as ImageIcon, Underline as UnderlineIcon, Strikethrough, Code, Quote, Minus, AlignLeft, AlignCenter, AlignRight, Type, RotateCcw, RotateCw, Eraser, CheckCircle2, Sparkles } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import PortalLayout from '../../../components/PortalLayout';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TipTapLink from '@tiptap/extension-link';
import TipTapImage from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import axios from 'axios';
import BlogAIGenerator from '../../../components/ai-blog/BlogAIGenerator';
import AIOutputReview from '../../../components/ai-blog/AIOutputReview';

const BlogEditor = () => {
  const { token } = useAuth();
  const toast = useToast();
  const { postId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [aiMode, setAiMode] = useState(null); // null | 'generating' | 'reviewing'
  const [generatedContent, setGeneratedContent] = useState(null);
  const [aiSuggestedCategory, setAiSuggestedCategory] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: '',
    tags: [],
    author: '',
    status: 'draft',
    published_at: new Date().toISOString().split('T')[0] // Today's date by default
  });

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  // Update slug when title changes (only for new posts)
  const handleTitleChange = (newTitle) => {
    setFormData(prev => ({
      ...prev,
      title: newTitle,
      slug: postId ? prev.slug : generateSlug(newTitle) // Only auto-generate for new posts
    }));
  };
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [updateConfirm, setUpdateConfirm] = useState({ isOpen: false });
  const [linkDialog, setLinkDialog] = useState({ isOpen: false, url: '' });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TipTapLink.configure({ openOnClick: false }),
      TipTapImage,
      TextAlign.configure({ types: ['heading', 'paragraph'] })
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    }
  });

  // Update editor when content changes (e.g., when loading existing post)
  useEffect(() => {
    if (editor && formData.content !== editor.getHTML()) {
      editor.commands.setContent(formData.content);
    }
  }, [formData.content, editor]);

  // Link dialog handlers
  const openLinkDialog = () => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkDialog({ isOpen: true, url: previousUrl || '' });
  };

  const confirmLink = () => {
    if (linkDialog.url) {
      // Ensure URL has protocol (https://) if it doesn't already
      let url = linkDialog.url.trim();
      if (url && !url.match(/^https?:\/\//i)) {
        url = 'https://' + url;
      }
      editor.chain().focus().setLink({ href: url }).run();
    }
    setLinkDialog({ isOpen: false, url: '' });
  };

  const cancelLink = () => {
    setLinkDialog({ isOpen: false, url: '' });
  };

  useEffect(() => {
    fetchCategories();
    if (postId) fetchPost();
  }, [postId]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/admin/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const post = response.data;
      if (post) {
        setFormData({
          title: post.title,
          slug: post.slug || generateSlug(post.title),
          excerpt: post.excerpt,
          content: post.content,
          featured_image: post.featured_image || '',
          category: post.category,
          tags: post.tags || [],
          author: post.author || '',
          status: post.status,
          published_at: post.published_at ? new Date(post.published_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });
        // Set uploaded filename if image exists
        if (post.featured_image) {
          // Extract filename from path if it's an uploaded image
          const match = post.featured_image.match(/\/([^\/]+)$/);
          if (match) {
            setUploadedFileName(match[1]);
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error loading post');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store the S3 URL and original filename
      const imageS3Url = response.data.url;
      setFormData(prev => ({...prev, featured_image: imageS3Url}));
      setUploadedFileName(file.name);
      toast.success('Image uploaded successfully to S3!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show confirmation for update only
    if (postId) {
      setUpdateConfirm({ isOpen: true });
    } else {
      // Create - no confirmation needed
      await savePost();
    }
  };

  const savePost = async () => {
    setLoading(true);

    try {
      const payload = {...formData};
      console.log('Saving post with payload:', { ...payload, content: '[content hidden]', featured_image: payload.featured_image });
      
      if (postId) {
        const response = await axios.put(`${BACKEND_URL}/api/blog/admin/posts/${postId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Post updated successfully, response:', response.data);
        toast.success('Blog post updated successfully!');
        // Stay in editor after update - don't navigate away
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/blog/admin/posts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Post created successfully, response:', response.data);
        toast.success('Blog post created successfully!');
        setTimeout(() => navigate('/portal/admin/blog'), 1500);
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Error saving post');
      setLoading(false);
    } finally {
      if (!postId) {
        // Only reset loading for new posts (which navigate away)
      } else {
        setLoading(false);
        setUpdateConfirm({ isOpen: false });
      }
    }
  };

  // AI Functions
  const handleAIGenerationComplete = (aiContent) => {
    setGeneratedContent(aiContent);
    setAiMode('reviewing');
  };

  const handleAcceptAIContent = async (editedContent, selectedLinks) => {
    // Convert AI content to blog post format
    let fullContent = editedContent.content.introduction;
    
    // Add all sections
    editedContent.content.sections.forEach(section => {
      fullContent += `<h2>${section.h2}</h2>${section.content}`;
    });
    
    // Add conclusion
    fullContent += editedContent.content.conclusion;
    
    // NOTE: Internal links are NOT auto-inserted to avoid malformed HTML
    // They're shown in the Internal Links tab for user to manually add if desired
    // This prevents double-linking and broken anchor tags
    
    // Add FAQs section if exists
    if (editedContent.faqs && editedContent.faqs.length > 0) {
      fullContent += '<h2>Frequently Asked Questions</h2>';
      editedContent.faqs.forEach(faq => {
        fullContent += `<h3>${faq.question}</h3><p>${faq.answer}</p>`;
      });
    }
    
    // Store AI suggested category for user to choose
    const suggestedCategory = editedContent.metadata.category;
    setAiSuggestedCategory(suggestedCategory);
    
    // Update form data - DON'T set category yet, let user choose
    setFormData(prev => ({
      ...prev,
      title: editedContent.metadata.h1_title,
      slug: generateSlug(editedContent.metadata.h1_title),
      excerpt: editedContent.metadata.excerpt,
      content: fullContent,
      category: '', // Leave empty for user to choose
      tags: editedContent.metadata.tags || [],
      published_at: prev.published_at || new Date().toISOString().split('T')[0],
      author: editedContent.author || ''
    }));
    
    // Exit AI mode
    setAiMode(null);
    setGeneratedContent(null);
    
    toast.success('AI content loaded! Choose a category below.');
  };

  const handleCancelAI = () => {
    setAiMode(null);
    setGeneratedContent(null);
  };

  const handleRegenerateAI = () => {
    setAiMode('generating');
    setGeneratedContent(null);
  };

  return (
    <>
      {/* AI Mode - Full Screen Overlays */}
      {aiMode === 'generating' && (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
          <div className="container mx-auto py-12">
            <BlogAIGenerator
              token={token}
              onGenerationComplete={handleAIGenerationComplete}
              onCancel={handleCancelAI}
            />
          </div>
        </div>
      )}

      {aiMode === 'reviewing' && generatedContent && (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
          <div className="container mx-auto py-12">
            <AIOutputReview
              generatedContent={generatedContent}
              onAccept={handleAcceptAIContent}
              onCancel={handleCancelAI}
              onRegenerate={handleRegenerateAI}
            />
          </div>
        </div>
      )}

      {/* Regular Editor */}
      <PortalLayout>
        <div className="p-8">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/portal/admin/blog')}
                  className="h-10"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{postId ? 'Edit' : 'Create'} Blog Post</h1>
                </div>
              </div>
              
              {/* AI Create Button (only for new posts) */}
              {!postId && !aiMode && (
                <motion.button
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setAiMode('generating')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
                >
                  <Sparkles className="w-5 h-5" />
                  Create with AI
                </motion.button>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                    <input type="text" required value={formData.title} onChange={(e) => handleTitleChange(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg font-semibold" placeholder="Enter an engaging title" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Slug</label>
                    <input type="text" required value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm" placeholder="article-url-slug" />
                    <p className="text-xs text-gray-500 mt-1">URL: /blog/{formData.slug || 'your-article-slug'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Excerpt</label>
                    <textarea required value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Brief summary (will appear in blog cards)" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Content *</label>
                    
                    {/* TipTap Comprehensive Toolbar */}
                    {editor && (
                      <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-3">
                        <div className="flex flex-wrap gap-1">
                          {/* Paragraph & Headings */}
                          <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('paragraph') ? 'bg-blue-100' : ''}`} title="Paragraph">
                            <Type className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('heading', { level: 1 }) ? 'bg-blue-100' : ''}`} title="Heading 1">
                            <Heading1 className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('heading', { level: 2 }) ? 'bg-blue-100' : ''}`} title="Heading 2">
                            <Heading2 className="w-4 h-4" />
                          </button>
                          <div className="w-px bg-gray-300 mx-1" />
                          
                          {/* Text Formatting */}
                          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('bold') ? 'bg-blue-100' : ''}`} title="Bold">
                            <Bold className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('italic') ? 'bg-blue-100' : ''}`} title="Italic">
                            <Italic className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('underline') ? 'bg-blue-100' : ''}`} title="Underline">
                            <UnderlineIcon className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('strike') ? 'bg-blue-100' : ''}`} title="Strikethrough">
                            <Strikethrough className="w-4 h-4" />
                          </button>
                          <div className="w-px bg-gray-300 mx-1" />
                          
                          {/* Lists */}
                          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('bulletList') ? 'bg-blue-100' : ''}`} title="Bullet List">
                            <List className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('orderedList') ? 'bg-blue-100' : ''}`} title="Numbered List">
                            <ListOrdered className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('blockquote') ? 'bg-blue-100' : ''}`} title="Quote">
                            <Quote className="w-4 h-4" />
                          </button>
                          <div className="w-px bg-gray-300 mx-1" />
                          
                          {/* Insert */}
                          <button type="button" onClick={openLinkDialog} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('link') ? 'bg-blue-100' : ''}`} title="Add Link">
                            <LinkIcon className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive('codeBlock') ? 'bg-blue-100' : ''}`} title="Code Block">
                            <Code className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} className="p-2 rounded hover:bg-gray-200 transition" title="Horizontal Line">
                            <Minus className="w-4 h-4" />
                          </button>
                          <div className="w-px bg-gray-300 mx-1" />
                          
                          {/* Alignment */}
                          <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive({ textAlign: 'left' }) ? 'bg-blue-100' : ''}`} title="Align Left">
                            <AlignLeft className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive({ textAlign: 'center' }) ? 'bg-blue-100' : ''}`} title="Align Center">
                            <AlignCenter className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={`p-2 rounded hover:bg-gray-200 transition ${editor.isActive({ textAlign: 'right' }) ? 'bg-blue-100' : ''}`} title="Align Right">
                            <AlignRight className="w-4 h-4" />
                          </button>
                          <div className="w-px bg-gray-300 mx-1" />
                          
                          {/* Utilities */}
                          <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} className="p-2 rounded hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed" title="Undo">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} className="p-2 rounded hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed" title="Redo">
                            <RotateCw className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} className="p-2 rounded hover:bg-gray-200 transition" title="Clear Formatting">
                            <Eraser className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* TipTap Editor */}
                    <EditorContent 
                      editor={editor} 
                      className="border border-gray-300 rounded-b-lg min-h-[400px] prose max-w-none p-4 bg-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use the toolbar to format your content with headings, bold, italic, lists, and links</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - 1/3 width */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4">Publish Settings</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="published_at">Publish Date *</Label>
                    <input
                      type="date"
                      id="published_at"
                      value={formData.published_at ? new Date(formData.published_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                      onChange={(e) => setFormData({...formData, published_at: e.target.value})}
                      className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]"
                    />
                    <p className="text-xs text-gray-500">This date appears on the public blog page</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status *</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft (not visible to public)</SelectItem>
                        <SelectItem value="published">Published (live on blog)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">Choose whether this post is visible to the public</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    
                    {/* Show AI Suggestion if available */}
                    {aiSuggestedCategory && (
                      <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-purple-900 mb-1">✨ AI Suggested:</p>
                            <p className="text-sm font-medium text-purple-800">"{aiSuggestedCategory}"</p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={async () => {
                              // Check if category exists
                              const categoryExists = categories.some(cat => cat.name === aiSuggestedCategory);
                              
                              if (!categoryExists) {
                                try {
                                  // Create the category
                                  await axios.post(
                                    `${BACKEND_URL}/api/blog/admin/categories`,
                                    { name: aiSuggestedCategory },
                                    { headers: { Authorization: `Bearer ${token}` }}
                                  );
                                  toast.success(`Category "${aiSuggestedCategory}" created!`);
                                  
                                  // Refresh categories and wait for state update
                                  await fetchCategories();
                                  
                                  // Small delay to ensure state propagates
                                  await new Promise(resolve => setTimeout(resolve, 100));
                                } catch (error) {
                                  console.error('Error creating category:', error);
                                  toast.error('Failed to create category');
                                  return;
                                }
                              }
                              
                              // Set the category in form data
                              setFormData(prev => ({...prev, category: aiSuggestedCategory}));
                              
                              // Clear the AI suggestion
                              setAiSuggestedCategory(null);
                              
                              toast.success(`Category selected: "${aiSuggestedCategory}"`);
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            Use This
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <Select value={formData.category} onValueChange={(value) => {
                      setFormData({...formData, category: value});
                      setAiSuggestedCategory(null); // Clear AI suggestion when manually selecting
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">Categorize this post for better organization</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="author">Author</Label>
                    <input
                      type="text"
                      id="author"
                      value={formData.author}
                      onChange={(e) => setFormData({...formData, author: e.target.value})}
                      className="w-full h-10 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#39baeb] focus:border-[#39baeb]"
                      placeholder="e.g., John Smith, Sarah Jones"
                    />
                    <p className="text-xs text-gray-500">Author name for the blog post (optional)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags</Label>
                    <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg min-h-[80px] bg-gray-50">
                      {formData.tags && formData.tags.length > 0 ? (
                        formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, tags: formData.tags.filter((_, i) => i !== index)})}
                              className="hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400">No tags added yet</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">Tags from AI will appear here. You can remove them if needed.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Featured Image (Optional)</label>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <input 
                            type="text" 
                            value={formData.featured_image} 
                            onChange={(e) => setFormData(prev => ({...prev, featured_image: e.target.value}))} 
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" 
                            placeholder="Full image URL (S3, https://...) or use Upload button" 
                            readOnly={uploading}
                          />
                          {uploadedFileName && formData.featured_image.startsWith('http') && (
                            <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                              <CheckCircle2 className="w-3 h-3" />
                              <strong>Original file:</strong> {uploadedFileName}
                            </p>
                          )}
                        </div>
                        <div>
                          <input 
                            type="file" 
                            id="image-upload" 
                            accept="image/*" 
                            onChange={handleImageUpload} 
                            className="hidden" 
                          />
                          <label htmlFor="image-upload" className="cursor-pointer">
                            <div className="inline-flex items-center justify-center px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-sm transition-colors">
                              <Upload className="w-4 h-4 mr-2" />
                              {uploading ? 'Uploading...' : 'Select & Upload Image'}
                            </div>
                          </label>
                        </div>
                      </div>
                      {formData.featured_image && (
                        <div className="relative">
                          <img src={formData.featured_image.startsWith('http') ? formData.featured_image : `${BACKEND_URL}${formData.featured_image}`} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-gray-200" />
                          <button 
                            type="button"
                            onClick={() => {
                              setFormData({...formData, featured_image: ''});
                              setUploadedFileName('');
                            }}
                            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700 py-3">
                  <Save className="w-5 h-5 mr-2" />{loading ? 'Saving...' : postId ? 'Update Post' : 'Create Post'}
                </Button>
                {formData.title && (
                  <button
                    type="button"
                    onClick={() => {
                      // Open preview in new window with current content
                      const previewWindow = window.open('', '_blank');
                      previewWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>${formData.title} - Preview</title>
                          <meta name="viewport" content="width=device-width, initial-scale=1.0">
                          <style>
                            * { margin: 0; padding: 0; box-sizing: border-box; }
                            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; background: #f9fafb; }
                            .header { background: white; border-bottom: 1px solid #e5e7eb; padding: 1rem 2rem; }
                            .container { max-width: 800px; margin: 0 auto; padding: 40px 20px; background: white; margin-top: 2rem; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                            .badge { background: #dbeafe; color: #1e40af; padding: 6px 16px; border-radius: 9999px; font-size: 0.875rem; font-weight: 600; display: inline-block; margin-bottom: 1.5rem; }
                            h1 { font-size: 2.5rem; font-weight: bold; margin-bottom: 1rem; line-height: 1.2; color: #111827; }
                            .meta { color: #6b7280; margin-bottom: 2rem; font-size: 0.875rem; }
                            .featured-image { width: 100%; height: 400px; object-fit: cover; border-radius: 8px; margin-bottom: 2rem; }
                            .excerpt { font-size: 1.25rem; color: #4b5563; font-style: italic; margin-bottom: 2rem; padding-bottom: 2rem; border-bottom: 1px solid #e5e7eb; }
                            .content { line-height: 1.8; color: #374151; }
                            .content h1 { font-size: 2em; font-weight: bold; margin-top: 2rem; margin-bottom: 1rem; }
                            .content h2 { font-size: 1.75em; font-weight: bold; margin-top: 1.5rem; margin-bottom: 0.75rem; }
                            .content h3 { font-size: 1.5em; font-weight: bold; margin-top: 1.5rem; margin-bottom: 0.75rem; }
                            .content p { margin-bottom: 1.25rem; }
                            .content ul, .content ol { margin-bottom: 1.25rem; padding-left: 2rem; }
                            .content li { margin-bottom: 0.5rem; }
                            .content ul li { list-style-type: disc; }
                            .content ol li { list-style-type: decimal; }
                            .content a { color: #2563eb; text-decoration: underline; }
                            .content code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em; }
                            .preview-label { position: fixed; top: 20px; right: 20px; background: #fef3c7; color: #92400e; padding: 8px 16px; border-radius: 9999px; font-weight: 600; font-size: 0.875rem; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
                          </style>
                        </head>
                        <body>
                          <div class="preview-label">${formData.status === 'draft' ? 'DRAFT' : ''} PREVIEW</div>
                          <div class="header">
                            <img src="/ascarii-logo.png" alt="Ascarii" style="height: 32px;" />
                          </div>
                          <div class="container">
                            <div class="badge">${formData.category || 'Uncategorized'}</div>
                            <h1>${formData.title || 'Article Title'}</h1>
                            <div class="meta">
                              <span>Published: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            </div>
                            ${formData.featured_image ? `<img src="${formData.featured_image.startsWith('http') ? formData.featured_image : BACKEND_URL + formData.featured_image}" alt="${formData.title}" class="featured-image" />` : ''}
                            ${formData.excerpt ? `<p class="excerpt">${formData.excerpt}</p>` : ''}
                            <div class="content">${formData.content || '<p>Article content will appear here...</p>'}</div>
                          </div>
                        </body>
                        </html>
                      `);
                      previewWindow.document.close();
                    }}
                    className="flex-shrink-0"
                  >
                    <Button type="button" variant="outline" className="h-full">
                      <Eye className="w-5 h-5 mr-2" />Preview
                    </Button>
                  </button>
                )}
              </div>
              <Button type="button" onClick={() => navigate('/portal/admin/blog')} variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog Management
              </Button>
            </div>
          </div>
        </form>
      </div>
    </PortalLayout>

    {/* Link Dialog */}
      {linkDialog.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Link</h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">URL</label>
              <input
                type="url"
                value={linkDialog.url}
                onChange={(e) => setLinkDialog({ ...linkDialog, url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" onClick={cancelLink} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button type="button" onClick={confirmLink} className="flex-1 bg-blue-600 hover:bg-blue-700">
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
      
    {/* Update Confirmation Dialog */}
    <ConfirmationDialog
      isOpen={updateConfirm.isOpen}
      onClose={() => setUpdateConfirm({ isOpen: false })}
      onConfirm={savePost}
      title="Update Blog Post?"
      message={`Are you sure you want to update this blog post?\n\n"${formData.title}"\n\nStatus: ${formData.status}`}
      confirmText="Update Post"
      cancelText="Cancel"
      type="info"
    />
    </>
  );
};

export default BlogEditor;
